import { useAdminNotifications } from "@/hooks/useAdminNotifications";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Megaphone, Users, Calendar } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

export const BroadcastHistoryTable = () => {
  const { t } = useTranslation();
  const { broadcastHistory } = useAdminNotifications();

  if (broadcastHistory.isLoading) {
    return (
      <Card className="mb-24">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            {t('broadcastHistory.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!broadcastHistory.data || broadcastHistory.data.length === 0) {
    return (
      <Card className="mb-24">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            {t('broadcastHistory.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-8">
            {t('broadcastHistory.noNotifications')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-24">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" />
          {t('broadcastHistory.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('broadcastHistory.date')}</TableHead>
                <TableHead>{t('broadcastHistory.titleColumn')}</TableHead>
                <TableHead>{t('broadcastHistory.message')}</TableHead>
                <TableHead>{t('broadcastHistory.recipients')}</TableHead>
                <TableHead>{t('broadcastHistory.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {broadcastHistory.data.map((broadcast) => (
                <TableRow key={broadcast.id}>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm whitespace-nowrap">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {format(new Date(broadcast.sent_at), "dd/MM/yyyy HH:mm")}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{broadcast.title}</TableCell>
                  <TableCell className="max-w-xs truncate">{broadcast.message}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {broadcast.recipient_count}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        broadcast.status === 'sent' ? 'default' :
                        broadcast.status === 'partial' ? 'secondary' :
                        'destructive'
                      }
                    >
                      {broadcast.status === 'sent' ? t('broadcastHistory.statusSent') :
                       broadcast.status === 'partial' ? t('broadcastHistory.statusPartial') :
                       t('broadcastHistory.statusFailed')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
